/**
 * ONE Invoca `PNAPI.run()` per settled DOM change.
 *
 * Invoca's tag swaps every `.InvocaNumber` tel: anchor for a pool number when
 * `Invoca.PNAPI.run()` is called; each call re-scans the whole document and
 * POSTs to pnapi.invoca.net (three POSTs per run on wwwstg, measured
 * 2026-09-10). Until this module, every caller ran it on its own schedule -
 * each tel Button on mount (useInvocaNumber), each Experience / SlotExperience
 * mount plus every MutationObserver batch (SmileInvocaTelAutoTag), the header,
 * the location drawer, the office search, the map - and a /smile page paid
 * 6-18 runs during hydration for one swap's worth of work (perf baseline
 * 2026-09-10, ranked cause 9: 10 POSTs on production /smile/, 18 on
 * /smile/emergency/, 22 on staging /smile/emergency/).
 *
 * Every caller now asks for a run here; the requests made while the DOM is
 * still changing coalesce into one run once it has been quiet for SETTLE_MS
 * (a burst that never quiets still runs within MAX_WAIT_MS). The contract to
 * Invoca is unchanged: a run still follows every mount of a tel: anchor, so
 * every anchor is instrumented (the run scans the whole document, so one run
 * covers every anchor mounted before it), and the poll-until-the-tag-arrives
 * behaviour every caller carried is now a single shared poll. The tag's own
 * page-load event is Invoca's, fired by the tag itself; nothing here touches it.
 *
 * Pure module, no React: the hook and the components call requestInvocaPNAPIRun.
 */

/** Quiet time after the last request before the run fires. */
export const INVOCA_RUN_SETTLE_MS = 100;
/** Upper bound between the first request of a burst and its run. */
export const INVOCA_RUN_MAX_WAIT_MS = 1000;
/** How often to look for the tag while it has not loaded. */
export const INVOCA_TAG_POLL_MS = 500;

let runPending = false;
let settleTimer: ReturnType<typeof setTimeout> | null = null;
let maxWaitTimer: ReturnType<typeof setTimeout> | null = null;
let pollTimer: ReturnType<typeof setInterval> | null = null;

const tagLoaded = (): boolean =>
  globalThis.window !== undefined && typeof window.Invoca?.PNAPI?.run === "function";

function clearRunTimers(): void {
  if (settleTimer) {
    clearTimeout(settleTimer);
    settleTimer = null;
  }
  if (maxWaitTimer) {
    clearTimeout(maxWaitTimer);
    maxWaitTimer = null;
  }
}

function flush(): void {
  clearRunTimers();
  if (!runPending) return;
  if (!tagLoaded()) {
    // The tag went away between the request and the run (a test tearing it
    // down, or a blocked script): keep the request and wait for it again.
    startPoll();
    return;
  }
  runPending = false;
  try {
    window.Invoca!.PNAPI!.run();
  } catch (error) {
    console.error("Failed to run Invoca PNAPI:", error);
  }
}

function scheduleRun(): void {
  if (settleTimer) clearTimeout(settleTimer);
  settleTimer = setTimeout(flush, INVOCA_RUN_SETTLE_MS);
  if (!maxWaitTimer) maxWaitTimer = setTimeout(flush, INVOCA_RUN_MAX_WAIT_MS);
}

function startPoll(): void {
  if (pollTimer) return;
  pollTimer = setInterval(() => {
    if (!tagLoaded()) return;
    clearInterval(pollTimer!);
    pollTimer = null;
    if (runPending) scheduleRun();
  }, INVOCA_TAG_POLL_MS);
}

/**
 * Ask for a PNAPI run. Safe to call from any mount, effect or observer, any
 * number of times: the calls coalesce into one run per settled DOM change.
 * A no-op on the server.
 */
export function requestInvocaPNAPIRun(): void {
  if (globalThis.window === undefined) return;
  runPending = true;
  if (tagLoaded()) {
    scheduleRun();
  } else {
    startPoll();
  }
}

/** Test-only: drop the shared request, timers and poll between cases. */
export function __resetInvocaPNAPIRunSchedulerForTests(): void {
  runPending = false;
  clearRunTimers();
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}
